import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Eye, MousePointerClick, Users, TrendingUp, TrendingDown,
  Globe, Monitor, Smartphone, Tablet, ArrowUpRight,
  BarChart3, ExternalLink, Percent
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const DEVICE_COLORS = ['#8B5CF6', '#D946EF', '#06B6D4'];
const BROWSER_COLORS = ['#8B5CF6', '#D946EF', '#06B6D4', '#F59E0B', '#10B981', '#EF4444', '#6366F1', '#EC4899'];

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

function trendPct(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function formatNumber(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

const TrendBadge = ({ value }) => {
  if (value === 0) return <span className="text-xs text-zinc-500">--</span>;
  const isUp = value > 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(value)}%
    </span>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1E1E1E] border border-[#27272A] rounded-lg p-3 shadow-xl">
      <p className="text-zinc-400 text-xs mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const Analytics = () => {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics?period=${period}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = data?.summary || {};
  const timeseries = (data?.timeseries || []).map(d => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  const stats = [
    { label: 'Page Views', value: s.views || 0, prev: s.prev_views || 0, icon: Eye, color: '#8B5CF6' },
    { label: 'Link Clicks', value: s.clicks || 0, prev: s.prev_clicks || 0, icon: MousePointerClick, color: '#D946EF' },
    { label: 'Unique Visitors', value: s.unique_visitors || 0, prev: s.prev_unique || 0, icon: Users, color: '#06B6D4' },
    { label: 'Click Rate', value: s.ctr || 0, prev: s.prev_ctr || 0, icon: Percent, color: '#10B981', suffix: '%' },
  ];

  const devices = data?.devices || [];
  const browsers = data?.browsers || [];
  const referrers = data?.referrers || [];
  const countries = data?.countries || [];
  const topLinks = data?.top_links || [];
  const totalDeviceViews = devices.reduce((a, d) => a + d.count, 0) || 1;

  return (
    <div className="max-w-5xl" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Analytics</h1>
          <p className="text-[#A1A1AA] mt-1">Deep insights into your profile performance</p>
        </div>
        <div className="flex gap-1 bg-[#121212] border border-[#27272A] rounded-xl p-1" data-testid="period-selector">
          {PERIODS.map(p => (
            <button
              key={p.value}
              data-testid={`period-${p.value}`}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p.value
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/20'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, i) => {
          const trend = stat.suffix ? trendPct(stat.value, stat.prev) : trendPct(stat.value, stat.prev);
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-[#121212] border border-[#27272A] p-5"
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: stat.color + '20' }}>
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <TrendBadge value={trend} />
              </div>
              <p className="font-outfit font-bold text-2xl text-white">
                {stat.suffix ? stat.value : formatNumber(stat.value)}{stat.suffix || ''}
              </p>
              <p className="text-zinc-500 text-sm mt-1">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Views & Clicks Over Time */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 mb-6"
        data-testid="timeseries-chart"
      >
        <h2 className="font-outfit font-semibold text-lg text-white mb-1">Views & Clicks</h2>
        <p className="text-sm text-zinc-500 mb-6">Daily traffic over the selected period</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeseries}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D946EF" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#D946EF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#71717A', fontSize: 11 }}
                interval={period === '90d' ? 13 : period === '30d' ? 4 : 0}
              />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717A', fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="views" name="Views" stroke="#8B5CF6" fill="url(#gViews)" strokeWidth={2} />
              <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#D946EF" fill="url(#gClicks)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-6 mt-4">
          <span className="flex items-center gap-2 text-sm text-zinc-400">
            <span className="w-3 h-3 rounded-full bg-violet-500" /> Views
          </span>
          <span className="flex items-center gap-2 text-sm text-zinc-400">
            <span className="w-3 h-3 rounded-full bg-fuchsia-500" /> Clicks
          </span>
        </div>
      </motion.div>

      {/* Two column: Referrers + Devices/Browsers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Referrers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl bg-[#121212] border border-[#27272A] p-6"
          data-testid="referrers-chart"
        >
          <h2 className="font-outfit font-semibold text-lg text-white mb-1">Traffic Sources</h2>
          <p className="text-sm text-zinc-500 mb-4">Where your visitors come from</p>
          {referrers.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={referrers} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#71717A', fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="source"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                    width={100}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" name="Visits" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState icon={Globe} text="No referrer data yet" />
          )}
        </motion.div>

        {/* Devices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-2xl bg-[#121212] border border-[#27272A] p-6"
          data-testid="devices-chart"
        >
          <h2 className="font-outfit font-semibold text-lg text-white mb-1">Devices</h2>
          <p className="text-sm text-zinc-500 mb-4">Device breakdown of visitors</p>
          {devices.length > 0 ? (
            <div className="flex items-center gap-6">
              <div className="w-40 h-40 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={devices}
                      dataKey="count"
                      nameKey="device"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      strokeWidth={0}
                    >
                      {devices.map((_, i) => (
                        <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {devices.map((d, i) => {
                  const DevIcon = DEVICE_ICONS[d.device] || Monitor;
                  const pct = Math.round((d.count / totalDeviceViews) * 100);
                  return (
                    <div key={d.device} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] + '20' }}>
                        <DevIcon className="w-4 h-4" style={{ color: DEVICE_COLORS[i % DEVICE_COLORS.length] }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-white capitalize">{d.device}</span>
                          <span className="text-zinc-400">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <EmptyState icon={Monitor} text="No device data yet" />
          )}
        </motion.div>
      </div>

      {/* Two column: Browsers + Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Browsers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl bg-[#121212] border border-[#27272A] p-6"
          data-testid="browsers-chart"
        >
          <h2 className="font-outfit font-semibold text-lg text-white mb-4">Browsers</h2>
          {browsers.length > 0 ? (
            <div className="space-y-3">
              {browsers.map((b, i) => {
                const totalBViews = browsers.reduce((a, x) => a + x.count, 0) || 1;
                const pct = Math.round((b.count / totalBViews) * 100);
                return (
                  <div key={b.browser} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BROWSER_COLORS[i % BROWSER_COLORS.length] }} />
                    <span className="text-sm text-white flex-1">{b.browser}</span>
                    <span className="text-sm text-zinc-400">{b.count}</span>
                    <span className="text-xs text-zinc-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Globe} text="No browser data yet" />
          )}
        </motion.div>

        {/* Countries */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="rounded-2xl bg-[#121212] border border-[#27272A] p-6"
          data-testid="countries-chart"
        >
          <h2 className="font-outfit font-semibold text-lg text-white mb-4">Top Countries</h2>
          {countries.length > 0 ? (
            <div className="space-y-3">
              {countries.map((c, i) => {
                const totalC = countries.reduce((a, x) => a + x.count, 0) || 1;
                const pct = Math.round((c.count / totalC) * 100);
                return (
                  <div key={c.country} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400 w-6">{i + 1}</span>
                    <span className="text-sm text-white flex-1">{c.country}</span>
                    <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm text-zinc-400 w-12 text-right">{c.count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Globe} text="No location data yet" />
          )}
        </motion.div>
      </div>

      {/* Top Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-2xl bg-[#121212] border border-[#27272A] p-6"
        data-testid="top-links-table"
      >
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Top Performing Links</h2>
        {topLinks.length > 0 ? (
          <div className="space-y-2">
            {topLinks.map((link, i) => (
              <div
                key={link.id}
                className="flex items-center gap-4 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
              >
                <span className="w-7 h-7 rounded-lg bg-violet-500/20 text-violet-400 text-sm font-medium flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{link.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{link.url}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-violet-400 font-medium text-sm">{link.click_count}</span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-600" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={BarChart3} text="No click data yet" subtitle="Share your profile to start tracking" />
        )}
      </motion.div>
    </div>
  );
};

const EmptyState = ({ icon: Icon, text, subtitle }) => (
  <div className="text-center py-8">
    <Icon className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
    <p className="text-zinc-500 text-sm">{text}</p>
    {subtitle && <p className="text-zinc-600 text-xs mt-1">{subtitle}</p>}
  </div>
);

export default Analytics;
